import React, { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import {
  Card,
  Button,
  Dropdown,
  Alert,
  Spinner,
  Accordion,
  Form,
  Nav,
} from 'react-bootstrap'
import { arrayMove } from '@dnd-kit/sortable'
import { projectsAPI, workStagesAPI, stageWorksAPI, projectTabsAPI } from '../../services/api'
import SortableWorkStage from './SortableWorkStage'
import AddWorkStageModal from './AddWorkStageModal'
import EditWorksModal from './EditWorksModal'
import CreateChatModal from '../chats/CreateChatModal'

const ProjectShow = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [project, setProject] = useState(null)
  const [tabs, setTabs] = useState([])
  const [activeTabId, setActiveTabId] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [currentUser, setCurrentUser] = useState(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showCreateChatModal, setShowCreateChatModal] = useState(false)
  const [selectedWorkStage, setSelectedWorkStage] = useState(null)
  const [activeAccordionKey, setActiveAccordionKey] = useState(null)
  const [selectedStageIds, setSelectedStageIds] = useState([])

  useEffect(() => {
    loadProject()
    loadCurrentUser()
  }, [id])

  const loadProject = async () => {
    try {
      setLoading(true)
      const response = await projectsAPI.get(id)
      setProject(response.data.project)
      const loadedTabs = response.data.tabs || []
      setTabs(loadedTabs)
      if (loadedTabs.length > 0) {
        setActiveTabId((prev) => prev || loadedTabs[0].id)
      }
      setError(null)
    } catch (err) {
      setError('Ошибка загрузки проекта')
      console.error('Error loading project:', err)
    } finally {
      setLoading(false)
    }
  }

  const loadCurrentUser = async () => {
    try {
      const response = await fetch('/api/current_user.json')
      if (response.ok) {
        const data = await response.json()
        setCurrentUser(data)
      }
    } catch (err) {
      console.error('Error loading current user:', err)
    }
  }

  const activeTab = tabs.find((t) => t.id === activeTabId)
  const workStages = activeTab?.work_stages || []

  // Снимаем выделение с этапов, которых больше нет (удаление, смена раздела)
  useEffect(() => {
    setSelectedStageIds((prev) => {
      const next = prev.filter((sid) => workStages.some((ws) => ws.id === sid))
      return next.length === prev.length ? prev : next
    })
  }, [workStages])

  const updateTabWorkStages = (tabId, newWorkStages) => {
    setTabs((prevTabs) =>
      prevTabs.map((t) => (t.id === tabId ? { ...t, work_stages: newWorkStages } : t))
    )
  }

  const selectedStages = workStages.filter((ws) => selectedStageIds.includes(ws.id))

  const handleToggleStageSelection = (workStageId) => {
    setSelectedStageIds((prev) =>
      prev.includes(workStageId)
        ? prev.filter((sid) => sid !== workStageId)
        : [...prev, workStageId]
    )
  }

  const handleToggleAllStages = () => {
    setSelectedStageIds(
      selectedStages.length === workStages.length ? [] : workStages.map((ws) => ws.id)
    )
  }

  // Порядок в запросе — как в списке этапов, а не как нажимали чекбоксы
  const selectedStagesQuery = selectedStages
    .map((ws) => `stage_ids[]=${ws.id}`)
    .join('&')

  const selectedOfferUrl = (forForeman) =>
    `/projects/${id}/commercial_offer.pdf?tab_id=${activeTabId}&${selectedStagesQuery}${
      forForeman ? '&for_foreman=true' : ''
    }`

  const handleToggleCompletion = async (workStageId) => {
    try {
      await workStagesAPI.toggleCompletion(id, workStageId)
      await loadProject()
    } catch (err) {
      console.error('Error toggling completion:', err)
      alert('Ошибка при изменении статуса')
    }
  }

  const handleEdit = (workStage) => {
    setSelectedWorkStage(workStage)
    setShowEditModal(true)
  }

  const handleDelete = async (workStageId) => {
    if (!window.confirm('Удалить этап из проекта?')) {
      return
    }

    try {
      await workStagesAPI.delete(id, workStageId)
      await loadProject()
    } catch (err) {
      console.error('Error deleting work stage:', err)
      alert('Ошибка при удалении этапа')
    }
  }

  const handleAddWorkStage = async (workStageIds) => {
    try {
      await workStagesAPI.create(id, { work_stage_ids: workStageIds, project_tab_id: activeTabId })
      await loadProject()
      setShowAddModal(false)
    } catch (err) {
      console.error('Error adding work stages:', err)
      alert('Ошибка при добавлении этапов')
    }
  }

  const handleDeleteProject = async () => {
    if (!window.confirm('Вы уверены, что хотите удалить этот проект?')) {
      return
    }

    try {
      await projectsAPI.delete(id)
      navigate('/projects')
    } catch (err) {
      console.error('Error deleting project:', err)
      alert('Ошибка при удалении проекта')
    }
  }

  const handleReorderWorks = async (workStageId, activeId, overId) => {
    const workStage = workStages.find((ws) => ws.id === workStageId)
    if (!workStage || !workStage.works) return

    const oldIndex = workStage.works.findIndex((w) => w.id === activeId)
    const newIndex = workStage.works.findIndex((w) => w.id === overId)

    const newWorks = arrayMove(workStage.works, oldIndex, newIndex)

    updateTabWorkStages(
      activeTabId,
      workStages.map((ws) => (ws.id === workStageId ? { ...ws, works: newWorks } : ws))
    )

    try {
      const ids = newWorks.map((w) => w.id)
      await stageWorksAPI.reorder(id, workStageId, ids)
    } catch (err) {
      console.error('Error reordering works:', err)
      await loadProject()
      alert('Ошибка при изменении порядка работ')
    }
  }

  const handleAddTab = async () => {
    const additionalCount = tabs.filter((t) => t.tab_type === 'additional').length
    const name = `Доп работы ${additionalCount + 1}`
    try {
      const response = await projectTabsAPI.create(id, name)
      const newTab = response.data
      setTabs((prev) => [...prev, newTab])
      setActiveTabId(newTab.id)
    } catch (err) {
      console.error('Error creating tab:', err)
      alert('Ошибка при создании раздела')
    }
  }

  const handleRenameTab = async (tab) => {
    const newName = window.prompt('Название раздела:', tab.name)
    if (!newName || newName.trim() === tab.name) return

    try {
      await projectTabsAPI.update(id, tab.id, newName.trim())
      setTabs((prev) =>
        prev.map((t) => (t.id === tab.id ? { ...t, name: newName.trim() } : t))
      )
    } catch (err) {
      console.error('Error renaming tab:', err)
      alert('Ошибка при переименовании раздела')
    }
  }

  const handleDeleteTab = async (tab) => {
    if (!window.confirm(`Удалить раздел "${tab.name}" и все его этапы?`)) return

    try {
      await projectTabsAPI.delete(id, tab.id)
      const remaining = tabs.filter((t) => t.id !== tab.id)
      setTabs(remaining)
      if (activeTabId === tab.id) {
        setActiveTabId(remaining[0]?.id || null)
      }
    } catch (err) {
      console.error('Error deleting tab:', err)
      alert('Ошибка при удалении раздела')
    }
  }

  const displayPrice = (price, priceForForeman) => {
    if (!price) return '0 ₽'

    const isAdmin = currentUser?.roles?.includes('admin')
    if (isAdmin && priceForForeman !== price && priceForForeman > 0) {
      return (
        <>
          {price.toLocaleString('ru-RU')} ₽{' '}
          <span className="small">/ {priceForForeman.toLocaleString('ru-RU')} ₽</span>
        </>
      )
    }
    return `${(isAdmin ? price : priceForForeman).toLocaleString('ru-RU')} ₽`
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" />
      </div>
    )
  }

  if (error || !project) {
    return <Alert variant="danger">{error || 'Проект не найден'}</Alert>
  }

  return (
    <div>
      <div className="d-flex flex-row justify-content-between align-items-start mb-3 mb-md-4 gap-2">
        <h1 className="mb-0 fs-4 fs-md-3">Проект: {project.address}</h1>
        <div className="d-flex gap-2">
          {project.chat_id ? (
            <Link to={`/chats/${project.chat_id}`} className="btn btn-outline-primary btn-sm">
              Чат
            </Link>
          ) : (
            <Button variant="outline-primary" size="sm" onClick={() => setShowCreateChatModal(true)}>
              Создать чат
            </Button>
          )}
          <Link to="/projects" className="btn btn-secondary btn-sm">
            Назад
          </Link>
        </div>
      </div>

      <CreateChatModal
        show={showCreateChatModal}
        onHide={() => setShowCreateChatModal(false)}
        project={project}
      />

      <Card className="shadow-sm mb-4 border-0">
        <Card.Header className="bg-white border-bottom py-2 py-md-3">
          <div className="d-flex flex-row justify-content-between align-items-center gap-2">
            <h5 className="mb-0 text-dark fs-6 fs-md-5">Информация</h5>
            {currentUser?.roles?.includes('admin') && (
              <Dropdown>
                <Dropdown.Toggle variant="outline-secondary" size="sm">
                  Действия
                </Dropdown.Toggle>
                <Dropdown.Menu align="end">
                  <Dropdown.Item as={Link} to={`/projects/${id}/edit`}>
                    Редактировать
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={handleDeleteProject} className="text-danger">
                    Удалить проект
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            )}
          </div>
        </Card.Header>
        <Card.Body className="p-3 p-md-4">
          <div className="row g-4">
            <div className="col-md-6">
              <div className="info-item mb-3">
                <div className="text-muted small mb-1">Номер договора</div>
                <div className="fw-semibold fs-6">{project.contract_number}</div>
              </div>
              <div className="info-item mb-3">
                <div className="text-muted small mb-1">Адрес объекта</div>
                <div className="fw-semibold fs-6">{project.address}</div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="info-item mb-3">
                <div className="text-muted small mb-1">Заказчик</div>
                <div className="fw-semibold fs-6">{project.customer_name}</div>
              </div>
              <div className="info-item mb-3">
                <div className="text-muted small mb-1">Номер телефона заказчика</div>
                <div className="fw-semibold fs-6">{project.customer_phone}</div>
              </div>
            </div>
          </div>
          <div className="row mt-3">
            <div className="col-12">
              <div className="info-item pt-3 border-top">
                <div className="text-muted small mb-1">Общая стоимость</div>
                <div className="fw-bold fs-5 text-primary">
                  {displayPrice(project.total_price, project.total_price_for_foreman)}
                </div>
                {currentUser?.roles?.includes('admin') &&
                  project.total_price_for_foreman !== project.total_price &&
                  project.total_price_for_foreman > 0 && (
                    <div className="text-muted small mt-1">(обычная / для прораба)</div>
                  )}
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Tabs */}
      <div className="mb-0">
        <ul className="nav nav-tabs flex-nowrap" style={{ overflowX: 'auto', overflowY: 'visible' }}>
          {tabs.map((tab) => {
            const isActive = activeTabId === tab.id
            return (
              <li key={tab.id} className="nav-item">
                <div
                  className={`nav-link d-flex align-items-center gap-1 ${isActive ? 'active' : ''}`}
                  style={{ cursor: 'pointer', whiteSpace: 'nowrap', paddingRight: '6px', userSelect: 'none' }}
                  onClick={() => {
                    setActiveTabId(tab.id)
                    setActiveAccordionKey(null)
                    setSelectedStageIds([])
                  }}
                >
                  <span>{tab.name}</span>
                  {tab.tab_type === 'additional' && (
                    <Dropdown onClick={(e) => e.stopPropagation()}>
                      <Dropdown.Toggle
                        as="button"
                        className="btn btn-link p-0 ms-1 text-muted lh-1 border-0"
                        style={{ boxShadow: 'none', fontSize: '1rem', lineHeight: 1, verticalAlign: 'middle' }}
                      >
                        ⋮
                      </Dropdown.Toggle>
                      <Dropdown.Menu popperConfig={{ strategy: 'fixed' }}>
                        <Dropdown.Item onClick={() => handleRenameTab(tab)}>
                          Переименовать
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => handleDeleteTab(tab)} className="text-danger">
                          Удалить раздел
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  )}
                </div>
              </li>
            )
          })}
          <li className="nav-item">
            <button
              className="nav-link text-primary"
              style={{ whiteSpace: 'nowrap', border: 'none', background: 'none' }}
              onClick={handleAddTab}
            >
              + Доп работы
            </button>
          </li>
        </ul>
      </div>

      {/* Tab content */}
      <div className="border border-top-0 rounded-bottom p-3 p-md-4 mb-4">
        {activeTab && (
          <>
            {/* Tab header: documents + price + add stage */}
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <span className="fw-semibold">
                  {displayPrice(activeTab.total_price, activeTab.total_price_for_foreman)}
                </span>
                {currentUser?.roles?.includes('admin') &&
                  activeTab.total_price_for_foreman !== activeTab.total_price &&
                  activeTab.total_price_for_foreman > 0 && (
                    <span className="text-muted small">(обычная / для прораба)</span>
                  )}
                {workStages.length > 0 && (
                  <Form.Check
                    type="checkbox"
                    id={`select-all-stages-${activeTabId}`}
                    label="Выбрать все этапы"
                    className="small mb-0"
                    checked={selectedStages.length === workStages.length}
                    onChange={handleToggleAllStages}
                  />
                )}
              </div>
              <div className="d-flex gap-2">
                <Dropdown>
                  <Dropdown.Toggle variant="outline-primary" size="sm">
                    Документы
                  </Dropdown.Toggle>
                  <Dropdown.Menu align="end">
                    {currentUser?.roles?.includes('admin') && (
                      <Dropdown.Item
                        href={`/projects/${id}/commercial_offer.pdf?tab_id=${activeTabId}`}
                        target="_blank"
                      >
                        Коммерческое предложение
                      </Dropdown.Item>
                    )}
                    <Dropdown.Item
                      href={`/projects/${id}/commercial_offer.pdf?tab_id=${activeTabId}&for_foreman=true`}
                      target="_blank"
                    >
                      Смета - М
                    </Dropdown.Item>
                    {currentUser?.roles?.includes('admin') && (
                      <Dropdown.Item
                        href={`/projects/${id}/completed_works_act.pdf?tab_id=${activeTabId}`}
                        target="_blank"
                      >
                        Акт выполненных работ
                      </Dropdown.Item>
                    )}
                    <Dropdown.Item
                      href={`/projects/${id}/completed_works_act.pdf?tab_id=${activeTabId}&for_foreman=true`}
                      target="_blank"
                    >
                      Акт выполненных работ - М
                    </Dropdown.Item>
                    <Dropdown.Item
                      href={`/projects/${id}/project_asr.pdf?tab_id=${activeTabId}`}
                      target="_blank"
                    >
                      АСР по разделу
                    </Dropdown.Item>
                    <Dropdown.Item
                      href={`/projects/${id}/project_materials.pdf?tab_id=${activeTabId}`}
                      target="_blank"
                    >
                      Материалы по разделу
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
                <Button variant="primary" size="sm" onClick={() => setShowAddModal(true)}>
                  Добавить этап работ
                </Button>
              </div>
            </div>

            {workStages.length === 0 ? (
              <div className="text-center py-5">
                <div className="text-muted mb-3">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2v20M2 12h20" />
                  </svg>
                </div>
                <p className="text-muted mb-0">
                  Этапы работ пока не добавлены. Нажмите &quot;Добавить этап работ&quot; для добавления первого этапа.
                </p>
              </div>
            ) : (
              <>
                {selectedStages.length > 0 && (
                  <div className="d-flex flex-wrap align-items-center gap-2 mb-3 p-2 border rounded bg-light">
                    <span className="fw-semibold small">
                      Выбрано этапов: {selectedStages.length}
                    </span>
                    <span className="small">
                      {displayPrice(
                        selectedStages.reduce((sum, ws) => sum + (ws.total_price || 0), 0),
                        selectedStages.reduce((sum, ws) => sum + (ws.total_price_for_foreman || 0), 0)
                      )}
                    </span>
                    <div className="d-flex flex-wrap gap-2 ms-md-auto">
                      {currentUser?.roles?.includes('admin') && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          href={selectedOfferUrl(false)}
                          target="_blank"
                        >
                          КП по выбранным
                        </Button>
                      )}
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        href={selectedOfferUrl(true)}
                        target="_blank"
                      >
                        Смета - М по выбранным
                      </Button>
                      <Button
                        variant="link"
                        size="sm"
                        className="text-decoration-none"
                        onClick={() => setSelectedStageIds([])}
                      >
                        Снять выделение
                      </Button>
                    </div>
                  </div>
                )}

                <Accordion
                  activeKey={activeAccordionKey}
                  onSelect={(key) => setActiveAccordionKey(key)}
                >
                  {workStages.map((workStage, index) => (
                    <SortableWorkStage
                      key={workStage.id}
                      workStage={workStage}
                      index={index}
                      project={project}
                      onToggleCompletion={handleToggleCompletion}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onReorderWorks={handleReorderWorks}
                      currentUser={currentUser}
                      onCollapse={() => setActiveAccordionKey(null)}
                      selected={selectedStageIds.includes(workStage.id)}
                      onToggleSelected={handleToggleStageSelection}
                    />
                  ))}
                </Accordion>
              </>
            )}
          </>
        )}
      </div>

      <AddWorkStageModal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        onAdd={handleAddWorkStage}
      />

      {selectedWorkStage && (
        <EditWorksModal
          show={showEditModal}
          onHide={async () => {
            setShowEditModal(false)
            setSelectedWorkStage(null)
            await loadProject()
          }}
          workStage={selectedWorkStage}
          project={project}
          currentUser={currentUser}
        />
      )}
    </div>
  )
}

export default ProjectShow
